import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  makeStyles,
  Collapse,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  FileCopy as CopyIcon,
  Send as SendIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
} from "@material-ui/icons";
import PixIcon from "@mui/icons-material/Pix";
import { toast } from "react-toastify";
import api from "../../services/api";
import useSettings from "../../hooks/useSettings";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#00BFA5",
    color: "#fff",
    padding: theme.spacing(1, 2),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  pixIcon: {
    fontSize: 28,
  },
  content: {
    padding: theme.spacing(2),
    minWidth: 380,
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  previewBox: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#e8f5e9",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid #00BFA5`,
  },
  previewTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#00BFA5",
    fontSize: "0.9rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  previewContent: {
    fontFamily: "inherit",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.95rem",
    lineHeight: 1.6,
  },
  editSection: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  editHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  editTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 500,
  },
  keyDisplay: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#fff",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  noKeyWarning: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ff9800",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    textAlign: "center",
    color: "#e65100",
  },
}));

const PIX_TYPES = [
  { value: "cpf", label: "CPF", placeholder: "000.000.000-00" },
  { value: "cnpj", label: "CNPJ", placeholder: "00.000.000/0000-00" },
  { value: "email", label: "E-mail", placeholder: "email@exemplo.com" },
  { value: "celular", label: "Celular", placeholder: "+55 11 99999-9999" },
  { value: "aleatoria", label: "Chave Aleatoria", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
];

const PixSendModal = ({ open, onClose, onSend, ticketId }) => {
  const classes = useStyles();
  const { update } = useSettings();

  const [pixType, setPixType] = useState("");
  const [pixKey, setPixKey] = useState("");
  const [pixName, setPixName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Carrega configuracoes salvas da empresa
  useEffect(() => {
    const loadSettings = async () => {
      if (!open) return;

      setLoadingSettings(true);
      setEditMode(false);
      try {
        const { data } = await api.get("/settings");

        const pixTypeConfig = data.find(s => s.key === "efipixtype");
        const pixKeyConfig = data.find(s => s.key === "efichavepix");
        const pixNameConfig = data.find(s => s.key === "efipixname");

        const type = pixTypeConfig?.value || "";
        const key = pixKeyConfig?.value || "";
        const name = pixNameConfig?.value || "";

        setPixType(type);
        setPixKey(key);
        setPixName(name);

        // Se nao tem chave configurada, abre em modo edicao
        if (!key) {
          setEditMode(true);
        }
      } catch (err) {
        console.error("Erro ao carregar configuracoes PIX:", err);
        setEditMode(true);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, [open]);

  const handleCopyKey = () => {
    if (!pixKey) {
      toast.info("Nenhuma chave PIX para copiar");
      return;
    }
    navigator.clipboard.writeText(pixKey);
    toast.success("Chave PIX copiada!");
  };

  const handleSaveSettings = async () => {
    if (!pixKey) {
      toast.error("Informe a chave PIX");
      return;
    }

    setSaving(true);
    try {
      await update({ key: "efipixtype", value: pixType });
      await update({ key: "efichavepix", value: pixKey });
      await update({ key: "efipixname", value: pixName });

      toast.success("Dados PIX salvos!");
      setEditMode(false);
    } catch (err) {
      toast.error("Erro ao salvar dados PIX");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getFormattedMessage = () => {
    const typeLabel = PIX_TYPES.find(t => t.value === pixType)?.label || "PIX";

    let message = `💠 *PIX*\n\n`;
    if (pixName) {
      message += `👤 *${pixName}*\n\n`;
    }
    message += `📋 *Chave ${typeLabel}:*\n`;
    message += `━━━━━━━━━━━━━━━━━━\n`;
    message += `${pixKey}\n`;
    message += `━━━━━━━━━━━━━━━━━━\n\n`;
    message += `📲 *Toque e segure na chave acima para copiar*\n\n`;
    message += `_Cole no app do seu banco para efetuar o pagamento via PIX._`;

    return message;
  };

  const handleSend = async () => {
    if (!pixKey) {
      toast.error("Configure a chave PIX antes de enviar");
      return;
    }

    setLoading(true);
    try {
      const typeLabel = PIX_TYPES.find(t => t.value === pixType)?.label || "PIX";

      // Monta mensagem formatada com a chave PIX
      let bodyText = `💠 *PIX*\n\n`;
      if (pixName) {
        bodyText += `👤 *${pixName}*\n\n`;
      }
      bodyText += `📋 *Chave ${typeLabel}:*\n\n`;
      bodyText += `\`\`\`${pixKey}\`\`\``;

      // Envia usando API de mensagem interativa com tipo pix_copy
      // O backend vai usar cta_copy com copy_code para o botão de copiar
      await api.post(`/tickets/${ticketId}/interactive`, {
        type: "pix_copy",
        bodyText,
        copyCode: pixKey,
      });

      toast.success("Chave PIX enviada!");
      onClose();
    } catch (err) {
      toast.error("Erro ao enviar chave PIX");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedType = PIX_TYPES.find(t => t.value === pixType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <PixIcon className={classes.pixIcon} />
          <Typography variant="h6">PIX Chat - Enviar Chave</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {loadingSettings ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Preview da Chave - sempre visivel quando tem chave */}
            {pixKey && !editMode && (
              <Box className={classes.previewBox}>
                <Typography className={classes.previewTitle}>
                  <PixIcon fontSize="small" />
                  Chave PIX configurada
                </Typography>
                <Box className={classes.keyDisplay}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {selectedType?.label || "PIX"}
                    </Typography>
                    <Typography style={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {pixKey}
                    </Typography>
                    {pixName && (
                      <Typography variant="caption" color="textSecondary">
                        Titular: {pixName}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={handleCopyKey} size="small" title="Copiar chave">
                    <CopyIcon />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Aviso quando nao tem chave */}
            {!pixKey && !editMode && (
              <Box className={classes.noKeyWarning}>
                <Typography variant="body1" gutterBottom>
                  Nenhuma chave PIX configurada
                </Typography>
                <Typography variant="body2">
                  Clique em "Editar" para configurar sua chave PIX
                </Typography>
              </Box>
            )}

            {/* Botao para expandir/retrair edicao */}
            <Box
              className={classes.editHeader}
              onClick={() => setEditMode(!editMode)}
              style={{ marginTop: pixKey && !editMode ? 0 : 16 }}
            >
              <Box className={classes.editTitle}>
                <EditIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  {editMode ? "Ocultar edicao" : "Editar dados PIX"}
                </Typography>
              </Box>
              {editMode ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            {/* Formulario de edicao */}
            <Collapse in={editMode}>
              <Box className={classes.editSection}>
                <FormControl variant="outlined" size="small" className={classes.formControl}>
                  <InputLabel>Tipo de Chave</InputLabel>
                  <Select
                    value={pixType}
                    onChange={(e) => setPixType(e.target.value)}
                    label="Tipo de Chave"
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {PIX_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={selectedType ? `Chave ${selectedType.label}` : "Chave PIX"}
                  placeholder={selectedType?.placeholder || "Digite a chave PIX"}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={pixKey}
                  onChange={(e) => setPixKey(e.target.value)}
                  className={classes.formControl}
                />

                <TextField
                  label="Nome do Titular"
                  placeholder="Nome completo do titular da conta"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={pixName}
                  onChange={(e) => setPixName(e.target.value)}
                  className={classes.formControl}
                />

                <Button
                  onClick={handleSaveSettings}
                  color="primary"
                  variant="outlined"
                  fullWidth
                  disabled={saving || !pixKey}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  Salvar dados PIX
                </Button>
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!pixKey || loading || editMode}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          style={{ backgroundColor: pixKey && !editMode ? "#00BFA5" : undefined, color: pixKey && !editMode ? "#fff" : undefined }}
        >
          Enviar para cliente
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PixSendModal;
